import { Link, useRouterState } from '@tanstack/react-router';
import { LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Me } from '../api.ts';
import { activeItemTo, itemsOf, locate, type NavArea } from '../navigation.ts';
import { usePlatformLook } from '../platform.tsx';
import { UserLinks } from './UserLinks.tsx';
import { AdminBadge } from './AdminBadge.tsx';

/**
 * Layout "Blocos de áreas" (ADR 0128): no Início, uma grade com as áreas que a pessoa pode usar;
 * dentro de uma área, uma faixa só com as telas dela e um botão que volta para os blocos.
 */
export function LauncherBar({
  areas,
  me,
  onSearch,
}: {
  areas: NavArea[];
  me: Me;
  onSearch: () => void;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const area = pathname === '/' ? undefined : locate(areas, pathname).area;
  const inside = area && area.id !== 'home' ? area : undefined;
  // A Configuração (área do rodapé) tem o próprio submenu na página: a faixa não o repete.
  const screens = inside && !inside.bottom ? itemsOf(inside) : [];
  const activeTo = activeItemTo(areas, pathname);
  return (
    <div className="ui-sidebar hidden shrink-0 items-center gap-2 border-b border-rule px-4 py-2 md:flex">
      <Link
        to="/"
        aria-label={inside ? t('nav.backToAreas') : t('nav.homeBrand')}
        title={inside ? t('nav.backToAreas') : undefined}
        className="flex min-h-9 shrink-0 items-center gap-2 rounded-md px-2 hover:bg-paper"
      >
        {inside ? (
          <LayoutGrid size={18} strokeWidth={1.75} aria-hidden />
        ) : (
          <img src={look.logoUrl} alt="" className="h-7 w-auto max-w-36 object-contain" />
        )}
      </Link>
      <AdminBadge compact={!!inside} />
      {inside && (
        <>
          <span className="shrink-0 px-2 text-sm font-semibold text-ink">{t(inside.key)}</span>
          <nav
            aria-label={t(inside.key)}
            className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
          >
            {screens.map((item) => {
              const active = item.to === activeTo;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={`flex min-h-9 shrink-0 items-center rounded-md px-3 text-sm whitespace-nowrap ${
                    active ? 'ui-nav-active font-medium' : 'text-ink hover:bg-paper'
                  }`}
                >
                  {t(item.key)}
                </Link>
              );
            })}
          </nav>
        </>
      )}
      {!inside && <span className="flex-1" />}
      <UserLinks areas={areas} me={me} onSearch={onSearch} />
    </div>
  );
}

/** A grade de áreas do Início, acima do conteúdo da página inicial. */
export function AreaTiles({ areas, style }: { areas: NavArea[]; style: 'cards' | 'icons' }) {
  const { t } = useTranslation();
  const tiles = areas.filter((a) => a.id !== 'home');
  if (tiles.length === 0) return null;
  const icons = style === 'icons';
  return (
    <nav aria-label={t('nav.label')}>
      <ul
        className={`grid gap-3 ${
          icons ? 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6' : 'grid-cols-2 lg:grid-cols-4'
        }`}
      >
        {tiles.map((area) => {
          const Icon = area.icon;
          const screens = itemsOf(area).slice(0, 3);
          return (
            <li key={area.id}>
              <Link
                to={itemsOf(area)[0]!.to}
                className={
                  icons
                    ? 'flex flex-col items-center gap-2 rounded-lg p-3 text-center text-sm text-ink hover:bg-sheet'
                    : 'ui-panel flex h-full flex-col gap-3 rounded-lg p-4 text-left hover:border-control-border'
                }
              >
                <span
                  className={`flex items-center justify-center rounded-lg ${
                    icons
                      ? 'size-16 bg-accent text-on-accent'
                      : 'size-11 bg-brand-soft text-brand-text'
                  }`}
                >
                  <Icon size={icons ? 28 : 22} strokeWidth={1.75} aria-hidden />
                </span>
                <span className="font-medium text-ink">{t(area.key)}</span>
                {!icons && (
                  <span className="text-xs text-muted">
                    {screens.map((s) => t(s.key)).join(' · ')}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
